import React, { useContext, SyntheticEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";

// Custom Components
import api from "@/lib/api";
import useFormController from "@/components/useFormController";
import DynamicDialog from "@/ui/Dialog/DynamicDialog";
import DialogHeader from "@/ui/Dialog/DialogHeader";
import DialogFooter from "@/ui/Dialog/DialogFooter";
import DialogToolbarButtonClose from "@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import DialogBody from "@/ui/Dialog/DialogBody";
import DialogActionButtonClose from "@/ui/Dialog/ActionButtons/DialogActionButtonClose";
import DialogActionButtonSave from "@/ui/Dialog/ActionButtons/DialogActionButtonSave";
import DialogToolbarMenuButton from "@/ui/Dialog/DialogToolbarMenuButton";
import DialogToolbarMenuItemActivate from "@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemActivate";
import DialogToolbarMenuItemDeactivate from "@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDeactivate";
import DialogContext from "@/ui/DialogContext/DialogContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import UserContext from "@/components/UserContext/UserContext";
import { GridContainer, GridItem } from "@/ui/Grid";
import FormContent from "@/components/FormContent";
import { Permissions } from "@/lib/permissions";
import FeatureMap from "@/views/GIS/MapPage/FeatureMap";
import StazaIcon from "@/icons/StazaIcon";
import FormAvatar from "@/ui/Form/FormAvatar";
import FormHeading from "@/ui/Form/FormHeading";
import FormHeadingExtendeData from "@/ui/Form/FormHeadingExtendedData";

// Types
import { ClosingDetails, FormControllerProps } from "@/@types/components/formController";
import { IDialogProps } from "@/@types/ui/DialogContext";
import { OwnerOrganisations } from "@/lib/ownerOrganisations";

const StazaDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const { dc, mode, form, onClose, preClose } = props;

  const { record, validation, dataChanged, fields, onFieldChange, doUpdate, doClose, doRefresh } =
    useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleUpdate = (evt: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          snackbarContext.showNotification("update", "success");
          close({ dataChanged: true, action: "update" });
        } else if (result.error) {
          const msg = t("error.editRecord") + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          // do nothing
        }
      })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, "error");
        });
    }
  };

  const handleClose = (evt: SyntheticEvent) => {
    if (doClose) {
      doClose().then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: "cancel" });
        } else {
          if (result.shouldSave) {
            handleUpdate(evt);
          } else if (result.canceled) {
            // do nothing
          }
        }
      });
    }
  };

  const close = (result: ClosingDetails) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
      if (preClose) {
        preClose(record);
      }
    }

    dialogContext.hideDialog();
  };

  const handleActivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        const apiInstance = new api();
        const url = `staze/${record.id}/activate`
        apiInstance.Call(url, "patch").then((result) => {
          if (result) {
            const msg = t("messages.activate_record_success");
            snackbarContext.showNotification(msg, "success");
            doRefresh();
            onClose({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.activate_record");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.activate_record");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };

  const handleDeactivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        const apiInstance = new api();
        const url = `staze/${record.id}/deactivate`

        apiInstance.Call(url, "patch").then((result) => {
          if (result) {
            const msg = t("messages.deactivate_record_success");
            snackbarContext.showNotification(msg, "success");
            close({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.deactiavate_record");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.deactivate_record");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };

  const recordOwnerId = record.owner_organisation as number; // 20 za hgss, 30 za hps

  const allowActivation = mode === "update" && ((userContext?.hasAnyPermission([Permissions.HGSSMod]) && recordOwnerId === OwnerOrganisations.HGSS.code)
    || (userContext?.hasAnyPermission([Permissions.HPSMod]) && recordOwnerId === OwnerOrganisations.HPS.code))


  const title = record.hps_naziv ? (record.hps_naziv as string + (!record.active ? ` - ${t('common.deactivated_record')}` : '')) : t("titles.staze") as string;
  const disableSave = !dataChanged;

  const calcMode = record.active === false ? "view" : mode;

  return (
    <DynamicDialog open maxWidth="xl" onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters>
          <FormAvatar icon={<StazaIcon />} ariaLabel="hiker" />
          <FormHeading header={title} subheaderContent={<FormHeadingExtendeData record={record} />} />
          <ToolbarFillContent />
          {allowActivation ? (
            <DialogToolbarMenuButton>
              {mode === "update" && allowActivation && record.active === true ? (
                <DialogToolbarMenuItemDeactivate onClick={handleDeactivate} />
              ) : null}
              {mode === "update" && allowActivation && record.active === false ? (
                <DialogToolbarMenuItemActivate onClick={handleActivate} />
              ) : null}
            </DialogToolbarMenuButton>

          ) : null}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box mt={2}>
          <GridContainer>
            <GridItem size={{ xs: 12, sm: 6 }}>
              <GridContainer>
                <FormContent
                  fieldNames={dc.getFormFieldsNames(form).filter((x: string) => x !== "id")}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={handleFieldChange}
                  mode={calcMode}
                  columns={2}
                />
              </GridContainer>
            </GridItem>
            <GridItem size={{ xs: 12, sm: 6 }}>
              {record ? <FeatureMap wkt={record.wkt as string} proj="EPSG:3765" mode="view" /> : null}
            </GridItem>
          </GridContainer>
        </Box>
      </DialogBody>
      <DialogFooter record={record}>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === "update" ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={disableSave} />
        ) : null}
      </DialogFooter>
    </DynamicDialog>
  );
};

export default StazaDialog;
